import React from "react"
import { graphql } from "gatsby"
import styled from "styled-components"

import Layout from "../layouts/Layout"
import Hero from "../components/careers/Hero"
import AboutUs from "../components/careers/AboutUs"
import HowWeWork from "../components/careers/HowWeWork"
import JobSlider from "../components/careers/JobSlider"
import EmployeeBenefits from "../components/careers/EmployeeBenefits"
import TeaserCollection from "../components/TeaserCollection"
import Team from "../components/careers/Team"
import { Container } from "../components/Grid"
import Button from "../components/Button"
import Snippet from "../components/Snippet"
import Spacer from "../components/Spacer"
import Seo from "../components/Seo"
import Cta from "../components/Cta"

import { mergeJobArrays } from "../helpers/careers"

const CtaRow = styled.div`
  text-align: center;
`

export const pageQuery = graphql`
  query CareersPageQuery($language: String, $originalPath: String) {
    contentfulPageMetadata(
      pagePath: { eq: $originalPath }
      node_locale: { eq: $language }
    ) {
      ...PageMetadata
    }
    jobDepartmentsEu {
      departments {
        name
        id
        jobs {
          title
          id
          absolute_url
          location {
            name
          }
          updated_at(locale: $language, formatString: "L")
        }
      }
    }
    # jobsContis: allFeedTeamTailorRss {
    #   nodes {
    #     guid
    #     department
    #     title
    #     locations {
    #       tt_location {
    #         tt_city
    #         tt_country
    #       }
    #     }
    #     link
    #   }
    # }
    allContentfulWorkMethod(
      sort: { sortOrder: ASC }
      filter: { node_locale: { eq: $language } }
    ) {
      nodes {
        title
        description {
          childMarkdownRemark {
            html
          }
        }
        icon: illustration {
          ...ContentfulImageData
        }
      }
    }
    careersBlog: allContentfulBlogPost(
      filter: {
        categories: { elemMatch: { slug: { eq: "careers" } } }
        node_locale: { eq: "en" }
      }
      limit: 3
      sort: { date: DESC }
    ) {
      nodes {
        id
        title
        slug
        image: coverImage {
          ...ContentfulImageData
          gatsbyImageData(
            height: 350
            width: 580
            layout: CONSTRAINED
            placeholder: BLURRED
          )
        }
      }
    }
    contentfulTeamMemberCollection(
      codeId: { eq: "teamCareersFeatured" }
      node_locale: { eq: $language }
      teamMembers: { elemMatch: { current: { eq: true } } }
    ) {
      teamMembers {
        nameInformal
        descriptionShort
        title
        picture {
          ...ContentfulImageData
          gatsbyImageData(
            cropFocus: FACE
            height: 240
            resizingBehavior: THUMB
            width: 240
          )
        }
      }
    }
    contentfulBenefitCollection(
      codeId: { eq: "benefitCollectionCareers" }
      node_locale: { eq: $language }
    ) {
      benefits {
        title
        icon {
          ...ContentfulImageData
        }
      }
    }
  }
`

const CareersPage = (props) => {
  const pageMetadata = props.data.contentfulPageMetadata
  const workMethods = props.data.allContentfulWorkMethod.nodes
  const blogPosts = props.data.careersBlog.nodes
  const team = props.data.contentfulTeamMemberCollection.teamMembers
  const benefits = props.data.contentfulBenefitCollection.benefits
  const fallbackDepartment = "Other"

  const blogPostsPrefixed = blogPosts.map((post) => ({
    ...post,
    link: `/blog/${post.slug}/`,
  }))

  const departments = mergeJobArrays(
    {
      data: props.data.jobDepartmentsEu?.departments,
      schema: {
        department: (item) => item.name || fallbackDepartment,
        jobs: (item) =>
          item.jobs.map((job) => ({
            id: job.id,
          })),
      },
    }
    // {
    //   data: props.data.jobsContis.nodes,
    //   schema: {
    //     department: (item) => item.department || fallbackDepartment,
    //     jobs: (item) => [
    //       {
    //         id: item.guid,
    //       },
    //     ],
    //   },
    // }
  )

  return (
    <Layout pageId="careers">
      <Seo {...pageMetadata} />
      <Hero showButton={departments.length >= 1} />
      <Spacer height={60} lg={80} xl={95} />
      <AboutUs />
      <Spacer height={70} md={100} xl={40} />
      {departments.length >= 1 ? <JobSlider departments={departments} /> : null}
      <Spacer height={70} md={100} xl={105} />
      <EmployeeBenefits benefits={benefits} />
      <Spacer height={60} md={80} xl={95} />
      <HowWeWork workMethods={workMethods} />
      <Container>
        <CtaRow>
          <Button as="a" href="#jobs" arrow>
            <Snippet id="careersJobsButton" field="linkText" />
          </Button>
        </CtaRow>
      </Container>
      <Spacer height={70} md={100} xl={165} />
      <Container>
        <TeaserCollection
          buttonText={Snippet({
            id: "careersBlog",
            field: "linkText",
          })}
          buttonLink={Snippet({ id: "careersBlog", field: "linkUrl" })}
          header={Snippet({ id: "careersBlog", field: "header" })}
          items={blogPostsPrefixed}
        />
      </Container>
      <Spacer height={70} md={100} xl={165} />
      <Team team={team} />
      <Cta
        header={Snippet({ id: "careersCTA", field: "header" })}
        linkText={Snippet({ id: "careersCTA", field: "linkText" })}
        linkUrl={Snippet({ id: "careersCTA", field: "linkUrl" })}
        highlightButton
      />
    </Layout>
  )
}

export default CareersPage
